import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  TextField,
  Typography,
  Collapse,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  ShoppingCart as CartIcon,
  Email as EmailIcon,
  Storefront as StoreIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';
import { API_BASE_URL } from '../../config';

interface CartItem {
  _id: string;
  assetId: string;
  assetName: string;
  assetFileName: string;
  quantity: number;
  stockLevel: number;
  addedAt: string;
  previewPath?: string;
  isAvailable: boolean;
}

interface CheckoutData {
  customerName: string;
  customerEmail: string;
  customerMessage: string;
}

// Local quantity input to avoid focus loss on re-render
const QuantityInput: React.FC<{
  item: CartItem;
  onUpdate: (itemId: string, quantity: number) => void;
}> = ({ item, onUpdate }) => {
  const [localQuantity, setLocalQuantity] = useState(item.quantity);

  useEffect(() => {
    setLocalQuantity(item.quantity);
  }, [item.quantity]);

  return (
    <TextField
      type="number"
      value={localQuantity}
      onChange={(e) => {
        const n = parseInt(e.target.value, 10);
        setLocalQuantity(Number.isFinite(n) ? n : 1);
      }}
      onBlur={() => {
        const validQuantity = Math.max(1, Math.min(localQuantity, item.stockLevel || localQuantity));
        setLocalQuantity(validQuantity);
        if (validQuantity !== item.quantity) onUpdate(item._id, validQuantity);
      }}
      size="small"
      inputProps={{ min: 1, max: item.stockLevel || undefined, style: { textAlign: 'center' } }}
      sx={{ width: 72 }}
    />
  );
};

export default function CartScreen(props: {
  onCartCountChange: (count: number) => void;
}) {
  const { onCartCountChange } = props;

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState<CheckoutData>({
    customerName: '',
    customerEmail: '',
    customerMessage: '',
  });
  const [submittingOrder, setSubmittingOrder] = useState(false);

  const getTotalItems = () => cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  const fetchCart = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/api/cart`);
      const items = Array.isArray(response.data) ? response.data : [];
      setCartItems(items);
      onCartCountChange(items.reduce((sum: number, it: CartItem) => sum + (it.quantity || 0), 0));
    } catch (e) {
      console.error('Error fetching cart:', e);
      setError('Kunde inte ladda varukorg');
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId: string, quantity: number) => {
    try {
      await axiosInstance.put(`/api/cart/${itemId}`, { quantity });
      await fetchCart();
    } catch (err: any) {
      console.error('Error updating cart item:', err);
      setError(err.response?.data?.error || 'Kunde inte uppdatera varukorg');
    }
  };

  const removeFromCart = async (itemId: string) => {
    try {
      await axiosInstance.delete(`/api/cart/${itemId}`);
      await fetchCart();
    } catch (err: any) {
      console.error('Error removing from cart:', err);
      setError(err.response?.data?.error || 'Kunde inte ta bort från varukorg');
    }
  };

  const clearCart = async () => {
    if (cartItems.length === 0) return;
    try {
      await Promise.all(cartItems.map((item) => axiosInstance.delete(`/api/cart/${item._id}`)));
      await fetchCart();
      setSuccess('Varukorgen har tömts');
    } catch (err: any) {
      console.error('Error clearing cart:', err);
      setError(err.response?.data?.error || 'Kunde inte tömma varukorg');
    }
  };

  const submitOrder = async () => {
    if (!checkoutData.customerName || !checkoutData.customerEmail) {
      setError('Namn och e-post är obligatoriska');
      return;
    }
    try {
      setSubmittingOrder(true);
      await axiosInstance.post(`/api/cart/checkout`, checkoutData);
      setSuccess('Beställning skickad! Du kommer att få en bekräftelse via e-post.');
      setCheckoutOpen(false);
      setCheckoutData({ customerName: '', customerEmail: '', customerMessage: '' });
      await fetchCart();
    } catch (err: any) {
      console.error('Error submitting order:', err);
      setError(err.response?.data?.error || 'Kunde inte skicka beställning');
    } finally {
      setSubmittingOrder(false);
    }
  };

  const openEmailClient = () => {
    const orderDetails = cartItems
      .map((item) => `${item.assetName} - Antal: ${item.quantity}`)
      .join('\n');
    const subject = encodeURIComponent(`Beställning från ${checkoutData.customerName || 'Kund'}`);
    const body = encodeURIComponent(`Hej!

Jag skulle vilja beställa följande artiklar:

${orderDetails}

Kontaktuppgifter:
Namn: ${checkoutData.customerName}
E-post: ${checkoutData.customerEmail}

${checkoutData.customerMessage ? `Meddelande:\n${checkoutData.customerMessage}` : ''}

Tack!
`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  useEffect(() => {
    fetchCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 5000);
    return () => clearTimeout(t);
  }, [success]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CartIcon />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {checkoutOpen ? 'Skicka beställning' : `Varukorg (${getTotalItems()})`}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : cartItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">Varukorgen är tom</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Lägg till artiklar från butiksmaterial-kategorin.
            </Typography>
          </Box>
        ) : (
          <>
            <List disablePadding sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
              {cartItems.map((item, idx) => (
                <React.Fragment key={item._id}>
                  <ListItem sx={{ py: 1.5, px: 1.5 }} disableGutters>
                    <Box sx={{ display: 'flex', gap: 1.5, width: '100%', alignItems: 'flex-start' }}>
                      <Avatar
                        src={item.previewPath ? `${API_BASE_URL}${item.previewPath}` : undefined}
                        variant="rounded"
                        sx={{ width: 64, height: 64, borderRadius: 2, bgcolor: 'grey.200' }}
                      >
                        {!item.previewPath && <StoreIcon sx={{ color: 'grey.600' }} />}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                          {item.assetName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }} noWrap>
                          {item.assetFileName}
                        </Typography>
                        <Chip
                          label={`${item.stockLevel} i lager`}
                          size="small"
                          sx={{
                            mt: 0.75,
                            bgcolor: item.stockLevel > 50 ? 'success.light' : item.stockLevel >= 10 ? 'warning.light' : 'error.light',
                            color: item.stockLevel > 50 ? 'success.dark' : item.stockLevel >= 10 ? 'warning.dark' : 'error.dark',
                            fontWeight: 600,
                          }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => updateCartItem(item._id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            aria-label="Minska"
                          >
                            <RemoveIcon />
                          </IconButton>
                          <QuantityInput item={item} onUpdate={updateCartItem} />
                          <IconButton
                            size="small"
                            onClick={() => updateCartItem(item._id, item.quantity + 1)}
                            disabled={item.quantity >= item.stockLevel}
                            aria-label="Öka"
                          >
                            <AddIcon />
                          </IconButton>
                          <Box sx={{ flex: 1 }} />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeFromCart(item._id)}
                            aria-label="Ta bort"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                  {idx < cartItems.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            <Collapse in={cartItems.length > 0 && !checkoutOpen}>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Totalt: <strong>{getTotalItems()}</strong> artiklar ({cartItems.length} rader)
                </Typography>
              </Box>
            </Collapse>

            <Collapse in={checkoutOpen} timeout={300}>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
                <Stepper activeStep={1} alternativeLabel sx={{ mb: 2 }}>
                  <Step completed><StepLabel>Varukorg</StepLabel></Step>
                  <Step><StepLabel>Kontakt</StepLabel></Step>
                  <Step><StepLabel>Bekräftelse</StepLabel></Step>
                </Stepper>

                <TextField
                  label="Namn *"
                  fullWidth
                  value={checkoutData.customerName}
                  onChange={(e) => setCheckoutData((p) => ({ ...p, customerName: e.target.value }))}
                  sx={{ mb: 2 }}
                  size="small"
                  disabled={submittingOrder}
                />
                <TextField
                  label="E-post *"
                  fullWidth
                  type="email"
                  value={checkoutData.customerEmail}
                  onChange={(e) => setCheckoutData((p) => ({ ...p, customerEmail: e.target.value }))}
                  sx={{ mb: 2 }}
                  size="small"
                  disabled={submittingOrder}
                />
                <TextField
                  label="Meddelande (valfritt)"
                  fullWidth
                  multiline
                  minRows={3}
                  value={checkoutData.customerMessage}
                  onChange={(e) => setCheckoutData((p) => ({ ...p, customerMessage: e.target.value }))}
                  size="small"
                  disabled={submittingOrder}
                />
              </Box>
            </Collapse>
          </>
        )}
      </Box>

      {/* Bottom actions (above bottom nav) */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        {!checkoutOpen ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteSweepIcon />}
              onClick={clearCart}
              disabled={cartItems.length === 0}
              fullWidth
            >
              Töm
            </Button>
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => setCheckoutOpen(true)}
              disabled={cartItems.length === 0}
              fullWidth
            >
              Gå till kassa
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setCheckoutOpen(false)}
              disabled={submittingOrder}
              fullWidth
            >
              ← Tillbaka
            </Button>
            <Button
              variant="outlined"
              startIcon={<LaunchIcon />}
              onClick={() => {
                if (checkoutData.customerName && checkoutData.customerEmail) {
                  openEmailClient();
                  setCheckoutOpen(false);
                } else {
                  setError('Namn och e-post krävs för att öppna mailprogrammet');
                }
              }}
              disabled={!checkoutData.customerName || !checkoutData.customerEmail}
              fullWidth
            >
              Mail
            </Button>
            <Button
              variant="contained"
              onClick={submitOrder}
              disabled={submittingOrder || !checkoutData.customerName || !checkoutData.customerEmail}
              startIcon={submittingOrder ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
              fullWidth
            >
              {submittingOrder ? 'Skickar…' : 'Skicka'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

